import { createContext, ReactNode, useContext } from "react";
import { LogisticsRequestQuote } from "../../../types/PartnerCompanyDetails";
import { AirPlaneIcon, ClockIcon, MapPinIcon, ShipIcon } from "../../Icons";
import { calculatePriceRange, parseServerDate, timeAgo } from "../../../utils";
import { getRFQStatusStyles } from "../../Admin/CustomerRFQsDetails/RFQCard";

const QuoteCardContext = createContext<LogisticsRequestQuote | null>(null);

const useQuoteCard = () => {
  const context = useContext(QuoteCardContext);
  if (!context) {
    throw new Error("QuoteCard components must be used inside RFQCard");
  }
  return context;
};

export default function QuoteCard({
  quote,
  onSelect,
  children,
}: {
  quote: LogisticsRequestQuote;
  onSelect: (quote: LogisticsRequestQuote) => void;
  children: ReactNode;
}) {
  return (
    <QuoteCardContext.Provider value={quote}>
      <div
        onClick={() => onSelect(quote)}
        className="p-4 bg-white border border-gray-100 rounded-xl transition-all duration-200 hover:border-gray-300 cursor-pointer"
      >
        {children}
      </div>
    </QuoteCardContext.Provider>
  );
}

QuoteCard.Header = function QuoteCardHeader({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <div className="flex justify-between items-start mb-2">{children}</div>
  );
};

QuoteCard.FromTo = function QuoteCardFromTo() {
  const quote = useQuoteCard();

  const from = quote.from_json?.name || "—";
  const to = quote.to_json?.name || "—";

  return (
    <div className="flex items-center gap-2 text-gray-700">
      <MapPinIcon className="size-4 text-gray-500" />
      <h3 className="text-sm font-semibold">
        {from} → {to}
      </h3>
    </div>
  );
};

QuoteCard.Status = function QuoteCardStatus() {
  const quote = useQuoteCard();

  const status = quote.status || "Pending";

  const { label, className } = getRFQStatusStyles(status);

  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium ${className}`}>
      {label}
    </span>
  );
};

QuoteCard.Body = function QuoteCardBody({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-between text-xs text-gray-500">
      {children}
    </div>
  );
};

QuoteCard.FreightInfo = function QuoteCardFreightInfo() {
  const quote = useQuoteCard();

  const freightType = quote.freight_type || "—";
  const weight = quote.weight ? `${quote.weight} kg` : "—";

  const FreightIcon =
    freightType.toLowerCase() === "air" ? AirPlaneIcon : ShipIcon;

  return (
    <div className="flex items-center gap-2">
      <FreightIcon className={`size-4 text-blue-500`} />
      <span>{freightType.toUpperCase()}</span>
      <span className="text-gray-400">|</span>
      <span>{weight}</span>
    </div>
  );
};

QuoteCard.PickupDate = function QuoteCardPickupDate() {
  const quote = useQuoteCard();

  const date = quote.pickup_date || "—";

  return <span className="text-[11px] text-gray-400">Pickup: {date}</span>;
};

QuoteCard.Footer = function QuoteCardFooter({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <div className="mt-3 flex justify-between items-center">{children}</div>
  );
};

QuoteCard.Price = function QuoteCardPrice() {
  const quote = useQuoteCard();

  const price = quote.ai_response?.quote?.estimated_price_usd || null;

  return price ? (
    <div className="flex items-baseline gap-1">
      <p className="text-sm font-semibold text-green-600">
        {calculatePriceRange(price).formatted("$")}
      </p>
      <span className="text-[11px] text-gray-400">(Estimated)</span>
    </div>
  ) : (
    <p className="text-xs text-gray-400 italic">Awaiting Quote</p>
  );
};

QuoteCard.CreatedAt = function QuoteCardCreatedAt() {
  const quote = useQuoteCard();

  const createdAt = quote.created_at;

  if (!createdAt) return null;

  return (
    <div className="flex items-center gap-1 text-[11px] text-gray-400">
      <ClockIcon className="size-3 text-gray-300" />
      <span>{timeAgo(parseServerDate(createdAt))}</span>
    </div>
  );
};
